import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";
import { Row, StatTile } from "../_ui";

const FREQ_OPTIONS = [
  { v: "breaking", label: "Breaking (30 min)", hint: "high-velocity finance / crypto / breaking news" },
  { v: "hourly",   label: "Hourly",            hint: "active news sites — most expensive" },
  { v: "standard", label: "Standard (4 hours)", hint: "default — balanced cost vs freshness" },
  { v: "slow",     label: "Slow (12 hours)",    hint: "low-volume niches" },
  { v: "daily",    label: "Daily",             hint: "evergreen / opinion / legal sites" },
  { v: "manual",   label: "Manual only",        hint: "no cron — you click Ingest yourself" },
];

/**
 * Content Velocity — ingest cadence + 24h pipeline stats + AI budget bar.
 * Embeds the R2 snapshot rebuild control inside its own "Outage resilience" Row.
 */
export default function ContentVelocityPanel({ site, form, setField }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  async function load() {
    setLoading(true); setLoadErr("");
    try {
      const r = await api.get(`/sites/${site.id}/velocity`);
      setData(r.data);
    } catch (err) {
      setLoadErr(err?.response?.data?.detail || err?.message || "Refresh failed");
    } finally { setLoading(false); }
  }
  useEffect(() => {
    let cancelled = false;
    api.get(`/sites/${site.id}/velocity`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [site.id]);

  const nextAt = data?.next_ingest_at ? new Date(data.next_ingest_at) : null;
  const minutesUntil = nextAt ? Math.max(0, Math.round((nextAt - new Date()) / 60000)) : null;
  const aiBudget = data?.ai_budget;
  const budgetUsed = aiBudget && aiBudget.daily_usd ? Math.min(100, Math.round((aiBudget.spent_today_usd / aiBudget.daily_usd) * 100)) : 0;
  const budgetOver = aiBudget && aiBudget.spent_today_usd >= aiBudget.daily_usd;

  return (
    <div className="space-y-4">
      <Row label="Ingest frequency">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            data-testid={SITEDETAIL.ingestFrequency}
            value={form.ingest_frequency}
            onChange={(e) => setField("ingest_frequency", e.target.value)}
            className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono"
          >
            {FREQ_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <span className="text-[11px] text-zinc-500">
            {FREQ_OPTIONS.find(o => o.v === form.ingest_frequency)?.hint}
          </span>
        </div>
      </Row>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
          <StatTile
            label="Ingested (24h)" value={data.counts_24h.ingested}
            sub={`${data.queue.pending} pending · ${data.queue.ready} ready`}
          />
          <StatTile
            label="Published (24h)" value={data.counts_24h.published}
            sub={data.auto_publish.enabled
              ? `auto: ${data.auto_publish.published_today}/${data.auto_publish.daily_cap}`
              : "auto-publish OFF"}
            accent={data.auto_publish.enabled ? "emerald" : "amber"}
          />
          <StatTile
            label="Sources" value={`${data.sources.enabled}/${data.sources.total}`}
            sub={data.sources.failing > 0 ? `${data.sources.failing} failing` : "all healthy"}
            accent={data.sources.failing > 0 ? "amber" : "zinc"}
          />
          <StatTile
            label="Next ingest"
            value={form.ingest_frequency === "manual" ? "—" : (minutesUntil != null ? `${minutesUntil}m` : "—")}
            sub={nextAt && form.ingest_frequency !== "manual" ? nextAt.toISOString().slice(11,16) + " UTC" : "manual mode"}
            accent={form.ingest_frequency === "manual" ? "zinc" : "indigo"}
          />
        </div>
      )}

      {data && (
        <div className="mt-3 border border-zinc-800 rounded-sm p-3 bg-zinc-900/40">
          <div className="flex items-center justify-between mb-2">
            <div className="label-tiny">AI Rewrite Budget (today)</div>
            <button onClick={load} disabled={loading} className="text-[11px] text-zinc-500 hover:text-white mono inline-flex items-center gap-1" data-testid={SITEDETAIL.velocityRefresh}>
              {loading ? <Loader2 className="animate-spin" size={10} /> : <RefreshCw size={10} />} refresh
            </button>
          </div>
          {loadErr && <div className="text-[11px] text-red-400 mono mb-2" data-testid="velocity-refresh-error">{loadErr}</div>}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-zinc-800 rounded-sm overflow-hidden">
              <div
                className={budgetOver ? "h-full bg-red-500" : budgetUsed > 80 ? "h-full bg-amber-500" : "h-full bg-emerald-500"}
                style={{ width: `${budgetUsed}%` }}
              />
            </div>
            <div className="mono text-xs text-zinc-300 min-w-[120px] text-right">
              ${aiBudget?.spent_today_usd?.toFixed(3)} / ${aiBudget?.daily_usd?.toFixed(2)}
            </div>
          </div>
          {budgetOver && (
            <div className="text-[11px] text-red-400 mono mt-2">
              Daily AI budget reached — new rewrites are paused until 00:00 UTC. Bump `ai_budget_daily_usd` to allow more.
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-zinc-500 mono">
        Scheduler fires the full pipeline at the chosen cadence: <span className="text-zinc-300">ingest sources → AI rewrite → quality gates → auto-publish</span>. Sites with the same frequency are staggered automatically (hash-based offset) so 500 sites don&apos;t fire at once.
      </div>

      <Row label="Outage resilience">
        <SnapshotControls site={site} />
      </Row>
    </div>
  );
}

/* ---------- R2 snapshot rebuild button (sibling — used by ContentVelocity only) ---------- */
function SnapshotControls({ site }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  async function rebuild() {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await api.post(`/sites/${site.id}/snapshots/rebuild`);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="flex items-start gap-3 flex-wrap">
      <button
        data-testid={SITEDETAIL.snapshotsRebuild}
        onClick={rebuild}
        disabled={busy}
        className="inline-flex items-center gap-1.5 border border-zinc-800 hover:bg-zinc-900 disabled:opacity-50 px-3 py-2 rounded-sm text-xs"
      >
        {busy ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
        Rebuild R2 snapshots
      </button>
      <div className="text-[11px] text-zinc-500 mono flex-1 min-w-[280px]">
        Re-renders every published article + the homepage to R2. The Cloudflare Worker falls back to these snapshots if the backend is down — your public site stays up.
        {result && (
          <div className="mt-1 text-emerald-400">
            ✓ {result.article_ok}/{result.total_articles} articles + homepage snapshot done.
          </div>
        )}
        {error && <div className="mt-1 text-red-400">{error}</div>}
      </div>
    </div>
  );
}
